"use client"

import { useState, useEffect, useRef } from "react"
import { Phone, Mail, MapPin, Clock } from "lucide-react"
import ReCAPTCHA from "react-google-recaptcha"


const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: "",
  });

  // Initialize with null to indicate "not yet determined"
  const [cookiesAccepted, setCookiesAccepted] = useState(null);

  useEffect(() => {
    // Check if user has consented to cookies - only on client side
    try {
      const consent = localStorage.getItem("cookie-consent");
      // Set to true by default unless explicitly declined
      setCookiesAccepted(consent === "declined" ? false : true);
    } catch (error) {
      // If localStorage fails, default to true
      setCookiesAccepted(true);
    }
  }, []);

  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleRecaptchaChange = (token) => {
    setCaptchaToken(token);
    setCaptchaError("");
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!cookiesAccepted) {
      setFormStatus({
        submitted: true,
        success: false,
        message: "Veuillez accepter les cookies pour pouvoir utiliser le formulaire de contact."
      });
      return;
    }

    if (!captchaToken) {
      setCaptchaError("Veuillez confirmer que vous n'êtes pas un robot");
      return;
    }

    try {
      setFormStatus({
        submitted: true,
        success: null,
        message: "Envoi en cours..."
      });

      // Use environment variable or window.location to determine endpoint
      // This makes it more flexible for different environments
      const apiEndpoint = 
        process.env.NEXT_PUBLIC_API_ENDPOINT || 
        'https://api.zak-epaviste.fr/contact.php';

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            message: formData.message,
            recaptchaToken: captchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus({
          submitted: true,
          success: true,
          message: data.message
        });

        setFormData({
          name: "",
          email: "",
          message: "",
        });

        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken("");

        setFormStatus({
          submitted: true,
          success: true,
          message: "Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais."
        });
      } else {
        setFormStatus({
          submitted: true,
          success: false,
          message: data.message || "Une erreur est survenue. Veuillez réessayer."
        });
      }
    } catch (error) {
      setFormStatus({
        submitted: true,
        success: false,
        message: "Problème de connexion au serveur. Veuillez vérifier votre connexion internet et réessayer."
      });
      console.error('Erreur lors de l\'envoi du formulaire:', error);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="h-6 w-6 text-green-600" />,
      title: "Téléphone",
      details: ["+33 6 14 86 86 93"],
    },
    {
      icon: <Mail className="h-6 w-6 text-green-600" />,
      title: "Email",
      details: ["contact@zak-ferrailleur.fr"],
    },
    {
      icon: <MapPin className="h-6 w-6 text-green-600" />,
      title: "Zone d'intervention",
      details: ["Loire (42)", "Haute-Loire (43)", "Rhône (69)"],
    },
    {
      icon: <Clock className="h-6 w-6 text-green-600" />,
      title: "Horaires",
      details: ["Lun-Ven: 8h-18h", "Sam: 9h-16h", "Dim: Fermé"],
    },
  ]

  return (
    <section id="contact" className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Contactez Votre Ferrailleur Épaviste à Saint-Étienne</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Besoin d'un enlèvement d'épave gratuit ou d'informations sur le rachat de métaux à Saint-Étienne, dans la Loire, la Haute-Loire
            ou le Rhône ? Contactez Zak, votre ferrailleur épaviste professionnel.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 className="text-2xl font-bold mb-6">Envoyez-nous un message</h3>

              {formStatus.submitted && (
                <div
                  className={`mb-6 p-4 rounded-lg ${formStatus.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                >
                  {formStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} data-dashlane-scope="true">
                <div className="mb-6">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                    data-dashlane-rid="ignore"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                    data-dashlane-rid="ignore"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  ></textarea>
                </div>

                {/* Only render these components when cookiesAccepted state is determined (not null) */}
                {cookiesAccepted !== null && (
                  <>
                    {cookiesAccepted && (
                      <div className="mb-6">
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey="6LcSijgrAAAAADQd2BS5OFOzjr_BGKhW72xwa_DR"
                            onChange={handleRecaptchaChange}
                            theme="light"
                            size="normal"
                            hl="fr"
                            cookiepolicy="single-host-origin"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          En utilisant ce formulaire, vous acceptez notre <a href="/mentions-legales/" className="text-green-600 hover:underline">politique de confidentialité</a> et les cookies nécessaires au fonctionnement du reCAPTCHA.
                        </p>
                        {captchaError && (
                            <p className="text-red-500 text-sm mt-2">{captchaError}</p>
                        )}
                      </div>
                    )}

                    {!cookiesAccepted && (
                      <div className="mb-6 p-4 rounded-lg bg-yellow-100 text-yellow-700">
                        <p>Pour utiliser ce formulaire, vous devez accepter les cookies de notre site. Veuillez activer les cookies dans le bandeau en bas de page.</p>
                      </div>
                    )}
                  </>
                )}

                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition-colors"
                  aria-label="Envoyer le message de contact"
                >
                  Envoyer le message
                </button>
              </form>
            </div>
          </div>

          <div>
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 className="text-2xl font-bold mb-6">Informations de contact</h3>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex">
                    <div className="mr-4 mt-1">{info.icon}</div>
                    <div>
                      <h4 className="font-semibold text-lg">{info.title}</h4>
                      {info.details.map((detail, idx) => (
                          index === 0 ?
                              <a href={`tel:${detail}`} key={idx} className="text-gray-700">
                                {detail}
                              </a> :
                        <p key={idx} className="text-gray-700">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <h4 className="font-semibold text-lg mb-4">Suivez-nous</h4>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/share/194F5j2KNu/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook Zak Ferrailleur Épaviste"
                    className="bg-gray-200 hover:bg-green-500 hover:text-white p-2 rounded-full transition-colors"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54v-2.203c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-white rounded-lg shadow-md p-8">
              <h3 className="text-xl font-bold mb-4">Zone d'intervention</h3>
              <p className="text-gray-700 mb-4">
                Zak intervient dans les départements de la Loire, la Haute-Loire et le Rhône pour l'enlèvement de
                véhicules et le rachat de métaux.
              </p>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-medium">Départements couverts :</p>
                <p className="text-gray-700">42 (Loire), 43 (Haute-Loire), 69 (Rhône)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact
